/**
 * Free Gift — Cart Automation
 *
 * Reads window.ooiaFreeGiftSettings (injected by snippets/ooia-free-gift.liquid) and
 * automatically adds or removes the configured gift variant based on the cart threshold.
 *
 * Settings:
 *   variantId      {number}  Shopify variant ID of the gift product
 *   thresholdType  {string}  'cart_total' | 'item_count'
 *   threshold      {number}  Minimum value required to unlock the gift
 */
(function () {
  'use strict';

  const settings = window.ooiaFreeGiftSettings;
  if (!settings || !settings.variantId) return;

  const STORAGE_KEY = 'ooiaFreeGiftRemoved';

  // State
  let isProcessing = false;

  // ---------------------------------------------------------------------------
  // Persist the "user removed the gift" flag in sessionStorage so it survives
  // page reloads (the theme reloads on the /cart page after every line-item change).
  // ---------------------------------------------------------------------------

  function getUserRemovedGift() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch (_) {
      return false;
    }
  }

  function setUserRemovedGift(value) {
    try {
      if (value) {
        sessionStorage.setItem(STORAGE_KEY, '1');
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (_) {
      // private browsing — ignore
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  function findGiftItem(cart) {
    return (
      cart.items.find(
        (item) =>
          item.variant_id === settings.variantId &&
          item.properties &&
          item.properties['_gift'] === 'true'
      ) || null
    );
  }

  /**
   * Returns the comparable cart value for threshold checking.
   * Gift item is excluded so it doesn't count toward its own threshold.
   */
  function getCartValue(cart) {
    const nonGiftItems = cart.items.filter(
      (item) =>
        !(
          item.variant_id === settings.variantId &&
          item.properties &&
          item.properties['_gift'] === 'true'
        )
    );

    if (settings.thresholdType === 'cart_total') {
      // final_line_price is in cents (after discounts) — divide by 100 to match the customizer value
      return nonGiftItems.reduce((sum, item) => sum + item.final_line_price, 0) / 100;
    }

    // item_count: total quantity of non-gift items
    return nonGiftItems.reduce((sum, item) => sum + item.quantity, 0);
  }

  async function apiFetch(path, body) {
    const response = await fetch(`${Shopify.routes.root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw new Error(`[Free Gift] ${path} responded with ${response.status}`);
    }
    return response.json();
  }

  async function addGift() {
    await apiFetch('cart/add.js', {
      items: [{ id: settings.variantId, quantity: 1, properties: { _gift: 'true' } }],
    });
  }

  async function removeGift(lineKey) {
    await apiFetch('cart/change.js', { id: lineKey, quantity: 0 });
  }

  function refreshCart() {
    if (window.themeVariables && window.themeVariables.settings.pageType === 'cart') {
      window.location.reload();
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  }

  // ---------------------------------------------------------------------------
  // Direct click detection — catches the user's intent immediately, before any
  // async cart operations or page reloads can reset state.
  // ---------------------------------------------------------------------------

  document.addEventListener('click', (event) => {
    const link = event.target.closest('.ooia-gift-badge-row a[href*="/cart/change"]');
    if (link) {
      setUserRemovedGift(true);
    }
  }, true); // capture phase — fires before the theme's handler

  // ---------------------------------------------------------------------------
  // Core logic
  // ---------------------------------------------------------------------------

  async function checkAndUpdateGift(cart) {
    if (isProcessing) return;

    const cartValue = getCartValue(cart);
    const thresholdMet = cartValue >= settings.threshold;
    const giftItem = findGiftItem(cart);

    if (thresholdMet && !giftItem) {
      // Threshold met but no gift — add it, unless the customer explicitly removed it
      if (getUserRemovedGift()) return;

      isProcessing = true;
      try {
        await addGift();
        refreshCart();
      } catch (err) {
        console.error('[Free Gift] Failed to add gift:', err);
      } finally {
        isProcessing = false;
      }
    } else if (!thresholdMet && giftItem) {
      // Threshold no longer met — remove the gift automatically
      isProcessing = true;
      try {
        await removeGift(giftItem.key);
        setUserRemovedGift(false);
        refreshCart();
      } catch (err) {
        console.error('[Free Gift] Failed to remove gift:', err);
      } finally {
        isProcessing = false;
      }
    } else if (!thresholdMet && !giftItem) {
      // Threshold not met and no gift — clear the flag so the gift is
      // re-added fresh the next time the threshold is reached.
      setUserRemovedGift(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Event listeners
  // ---------------------------------------------------------------------------

  document.addEventListener('cart:change', async (event) => {
    const cart = event.detail && event.detail.cart;
    if (!cart || isProcessing) return;
    await checkAndUpdateGift(cart);
  });

  // ---------------------------------------------------------------------------
  // Initialise on page load
  // ---------------------------------------------------------------------------

  (async function init() {
    try {
      const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      await checkAndUpdateGift(cart);
    } catch (err) {
      console.error('[Free Gift] Initialisation failed:', err);
    }
  })();
})();
